/**
 * Production-Safe Logging Utility
 * Addresses BUG-M008 (Sensitive data in console logs)
 */

const IS_PRODUCTION = import.meta.env.MODE === 'production';
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';

// Sensitive keys that should never be logged
const SENSITIVE_KEYS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apiKey',
  'secret',
  'authorization',
  'auth',
  'session',
  'cookie',
  'credit_card',
  'ssn',
  'stripe',
];

/**
 * Redacts sensitive information from objects
 */
function redactSensitiveData(data: any): any {
  if (!data) return data;

  if (typeof data === 'string') {
    // Check if it looks like a token (long alphanumeric string)
    if (data.length > 30 && /^[a-zA-Z0-9_-]+$/.test(data)) {
      return '[REDACTED_TOKEN]';
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => redactSensitiveData(item));
  }

  if (typeof data === 'object') {
    const redacted: any = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_KEYS.some(sensitive =>
        lowerKey.includes(sensitive)
      );

      if (isSensitive) {
        redacted[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        redacted[key] = redactSensitiveData(value);
      } else {
        redacted[key] = value;
      }
    }
    return redacted;
  }

  return data;
}

/**
 * Production-safe logger
 */
export const logger = {
  /**
   * Debug logs - only in development
   */
  debug: (...args: any[]): void => {
    if (IS_DEVELOPMENT) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Info logs - only in development
   */
  info: (...args: any[]): void => {
    if (IS_DEVELOPMENT) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Warning logs - in development and production (redacted)
   */
  warn: (...args: any[]): void => {
    if (IS_DEVELOPMENT) {
      console.warn('[WARN]', ...args);
    } else if (IS_PRODUCTION) {
      const redacted = args.map(redactSensitiveData);
      console.warn('[WARN]', ...redacted);
    }
  },

  /**
   * Error logs - in development and production (redacted)
   * In production, this should also send to error tracking service
   */
  error: (message: string, error?: Error | any, context?: any): void => {
    if (IS_DEVELOPMENT) {
      console.error('[ERROR]', message, error, context);
    } else if (IS_PRODUCTION) {
      const redactedContext = context ? redactSensitiveData(context) : undefined;
      console.error('[ERROR]', message, redactedContext);

      // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
      // Example:
      // Sentry.captureException(error, {
      //   contexts: { additional: redactedContext },
      //   tags: { message }
      // });
    }
  },

  /**
   * User action logs - for analytics (only in development)
   */
  action: (actionName: string, data?: any): void => {
    if (IS_DEVELOPMENT) {
      console.log(`[ACTION] ${actionName}`, data);
    }
    // In production, this should send to analytics service
    // Example: analytics.track(actionName, data);
  },

  /**
   * API call logs - only in development
   */
  api: (method: string, url: string, data?: any): void => {
    if (IS_DEVELOPMENT) {
      console.log(`[API] ${method} ${url}`, data);
    }
  },

  /**
   * Performance logs - only in development
   */
  perf: (label: string, duration: number): void => {
    if (IS_DEVELOPMENT) {
      console.log(`[PERF] ${label}: ${duration}ms`);
    }
  }
};

/**
 * Performance timer utility
 */
export class PerfTimer {
  private start: number;
  private label: string;

  constructor(label: string) {
    this.label = label;
    this.start = performance.now();
  }

  end(): void {
    const duration = performance.now() - this.start;
    logger.perf(this.label, Math.round(duration));
  }
}

/**
 * Replaces console methods in production to prevent accidental logging
 */
export function disableConsoleLogs(): void {
  if (IS_PRODUCTION) {
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    // Keep console.warn and console.error but with redaction
    const originalWarn = console.warn;
    const originalError = console.error;

    console.warn = (...args: any[]) => {
      const redacted = args.map(redactSensitiveData);
      originalWarn.apply(console, redacted);
    };

    console.error = (...args: any[]) => {
      const redacted = args.map(redactSensitiveData);
      originalError.apply(console, redacted);
    };
  }
}

// Auto-disable console logs in production
if (IS_PRODUCTION) {
  disableConsoleLogs();
}
